import React from 'react';
import { Box } from '@mui/material';

// Mapping currency short codes or names to country ISO 2-letter codes for flagcdn.com
const COUNTRY_CODE_MAP = {
  USD: 'us',
  EUR: 'eu',
  GBP: 'gb',
  CAD: 'ca',
  AUD: 'au',
  AED: 'ae',
  TRY: 'tr',
  CHF: 'ch',
  JPY: 'jp',
  CNY: 'cn',
  SAR: 'sa',
  INR: 'in',
  RUB: 'ru',
  IQD: 'iq',
  KWD: 'kw',
  QAR: 'qa',
  OMR: 'om',
  BHD: 'bh',
  SGD: 'sg',
  HKD: 'hk',
  NZD: 'nz',
  SEK: 'se',
  NOK: 'no',
  DKK: 'dk',
  MYR: 'my',
  THB: 'th',
  KRW: 'kr',
  IRR: 'ir',
  TOMAN: 'ir',
  AFN: 'af',
  AZN: 'az',
  AMD: 'am',
};

// Helper to convert Unicode regional indicator pair emoji to 2-letter country code
const getCountryIsoFromEmoji = (emojiStr) => {
  if (!emojiStr || typeof emojiStr !== 'string') return null;
  const chars = Array.from(emojiStr);
  if (chars.length === 2) {
    const cp1 = chars[0].codePointAt(0);
    const cp2 = chars[1].codePointAt(0);
    if (cp1 >= 0x1F1E6 && cp1 <= 0x1F1FF && cp2 >= 0x1F1E6 && cp2 <= 0x1F1FF) {
      const c1 = String.fromCharCode(cp1 - 0x1F1E6 + 65);
      const c2 = String.fromCharCode(cp2 - 0x1F1E6 + 65);
      return (c1 + c2).toLowerCase();
    }
  }
  return null;
};

const FlagIcon = ({ short, shortCode, code: propCode, flag, size = 26 }) => {
  const raw = short || shortCode || propCode || '';
  const code = raw ? raw.toUpperCase().trim() : '';

  // Check if it's a gold or precious metal asset
  if (
    flag === '🪙' ||
    code.includes('GOLD') ||
    code.includes('BAHAR') ||
    code.includes('EMAMI') ||
    code.includes('GRAM') ||
    code.includes('SEKE') ||
    code.includes('AYAR') ||
    code.includes('NIM') ||
    code.includes('ROB') ||
    code.includes('GERAMI') ||
    code.includes('ABSHODEH') ||
    code.includes('OUNCE')
  ) {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: size,
          height: size,
        }}
      >
        <i className="fa-solid fa-coins" style={{ color: '#F59E0B', fontSize: size * 0.85 }} />
      </Box>
    );
  }

  // Country flag mapping (by code or converted from emoji)
  const countryIso = COUNTRY_CODE_MAP[code] || getCountryIsoFromEmoji(flag);

  if (countryIso) {
    const localSrc = `${process.env.PUBLIC_URL || ''}/flags/${countryIso}.png`;
    const remoteSrc = `https://flagcdn.com/w80/${countryIso}.png`;

    return (
      <Box
        component="img"
        src={localSrc}
        alt={`${code || countryIso} flag`}
        sx={{
          width: size,
          height: Math.round(size * 0.72),
          borderRadius: '3.5px',
          objectFit: 'cover',
          display: 'inline-block',
          verticalAlign: 'middle',
          boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
          border: '1px solid rgba(0,0,0,0.12)',
        }}
        onError={(e) => {
          if (!e.target.dataset.triedFallback) {
            e.target.dataset.triedFallback = '1';
            e.target.src = remoteSrc;
          } else {
            e.target.style.display = 'none';
          }
        }}
      />
    );
  }

  // Fallback to FontAwesome money icon or raw emoji
  if (flag && flag !== '🌐' && flag !== '🪙') {
    return <span style={{ fontSize: size * 0.85 }}>{flag}</span>;
  }

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
      }}
    >
      <i className="fa-solid fa-money-bill-trend-up" style={{ color: '#3B82F6', fontSize: size * 0.8 }} />
    </Box>
  );
};

export default FlagIcon;
